// @flow
import React from "react";
import {Modal, Row, Button, Typography, Select, Table} from "antd";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {
    CloseCircleOutlined,
    CheckCircleOutlined,
    ClearOutlined,
    CoffeeOutlined
} from "@ant-design/icons";
import {
    TableTitle,
    TableOverflow,
    DescriptionContainer,
    OrderNumber,
    OrderTotalTime,
    QueueModalContainer,
    PrepDetailContainer,
    SelectField
} from "../../../../order/orderDetail/orderDetailStyles.js";
import {OrderTable as OrderTableNew} from "../../../../order/orderDetail/orderDetailStyles";
import {calculateElapsedTime, formatPhoneNumber} from "../../../../../utils/helpers.js";
import {courierStatuses, courierStatusColors} from "../../../../../constants/constants.js";
import ProductTable from "../../../../_defaultComponents/ProductTable/ProductTable.js";
import {
    StyledQueueModal,
    CourierSection,
    CourierSectionRow,
    CourierSectionLabel,
    CourierButtonGroup
} from "./styles/QueueModal.styles.js";

const {Text} = Typography;
const {Column} = Table;

const CourierSelect = ({availableCouriers, excludeId, value, onChange, testId, placeholder}) => (
    <span data-testid={testId} style={{display: "block", width: "100%"}}>
        <SelectField
            placeholder={placeholder || "Atanacak Kurye"}
            value={value}
            onChange={onChange}
            loading={availableCouriers === null}
            disabled={availableCouriers === null}
            style={{width: "100%"}}
        >
            {availableCouriers?.map(courier => {
                if (courier.id != excludeId) {
                    return (
                        <Select.Option
                            key={courier.id}
                            value={courier.id}
                            style={{
                                color: courier.isMaximumWaiting
                                    ? courierStatusColors["maximumWaitingForBreak"]
                                    : courierStatusColors[courier.status]
                            }}
                        >
                            {courier.isMaximumWaiting && (
                                <CoffeeOutlined style={{color: "red", marginRight: "4px"}} />
                            )}
                            {courier.status === "onBreak" && (
                                <CoffeeOutlined style={{color: "orange", marginRight: "4px"}} />
                            )}
                            {`${courier.name} - ${
                                courierStatuses.find(c => c.value == courier.status).name
                            } - ${courier.distance?.toFixed(1)} km`}
                        </Select.Option>
                    );
                }
            })}
        </SelectField>
    </span>
);

export const QueueModal = ({
    visible,
    onCancel,
    queueModalData,
    preferredCourierValue,
    setPreferredCourierValue,
    forceCourierValue,
    setForceCourierValue,
    availableCouriers,
    updatePreferredCourier,
    removePreferredCourier,
    updateForceCourier,
    removeForceCourier,
    cancelDelivery,
    setModalVisible,
    setModalData,
    setModalType,
    setText,
    setSubmit,
    stores,
    data,
    queueModalIndex
}) => {
    const {t, i18n} = useTranslation(["deliveryStatuses", "orderDetail"]);

    const queueDeliveryType =
        queueModalData?.deliveryType === "productReturn" ? "return" : "delivery";

    const handleCancelDelivery = () => {
        setModalVisible(true);
        setModalData({
            type: "queue",
            deliveryId: queueModalData?._id
        });
        setModalType("cancel");
        setText({
            label: t("deliveryModal.cancelReasonLabel", {ns: "common"}),
            title: t("deliveryModal.cancelReasonTitle", {ns: "common"})
        });
        setSubmit(() => cancelDelivery);
    };

    return (
        <StyledQueueModal
            width={700}
            footer={null}
            visible={visible}
            data-testid="queueModal"
            onCancel={onCancel}
        >
            <QueueModalContainer>
                <OrderNumber>
                    {t("fields.orderNumber", {ns: "common"})}:{" "}
                    <span data-testid="orderNumber">
                        {queueModalData?.orders
                            ? queueModalData.orders.map(x => x.orderNumber).join(", ")
                            : "-"}
                    </span>
                </OrderNumber>
                {queueModalData?.orders &&
                queueModalData?.orders[0] &&
                queueModalData?.orders[0].platformPickingTime ? (
                    <PrepDetailContainer>
                        <TableTitle>{t("other.platformDetail", {ns: "common"})}</TableTitle>
                        <Table rowKey="_id" dataSource={queueModalData?.orders} pagination={false}>
                            <Column
                                title={t("other.platformPickingTime", {ns: "common"})}
                                render={text =>
                                    moment.utc(text.platformPickingTime).format("DD-MM-YYYY HH:mm")
                                }
                            />
                            <Column
                                title={t("other.platformDeliveryTime", {ns: "common"})}
                                render={text =>
                                    moment.utc(text.platformDeliveryTime).format("DD-MM-YYYY HH:mm")
                                }
                            />
                            <Column
                                title={""}
                                render={text =>
                                    text.isPlatformSlotOrder
                                        ? t("other.platformSlotDelivery", {ns: "common"})
                                        : t("other.platfromQuickDelivery", {ns: "common"})
                                }
                            />
                        </Table>
                    </PrepDetailContainer>
                ) : (
                    ""
                )}
                <Row>
                    <div>
                        <Text type="success">{t("fields.start", {ns: "common"})}: </Text>
                        <span data-testid="startTime">
                            {moment(queueModalData?.createdAt).format("HH:mm:ss")}
                        </span>
                    </div>
                </Row>
                <OrderTotalTime>
                    {t("fields.elapsedTime", {ns: "common"})}:{" "}
                    <span data-testid="elapsedTime">
                        {queueModalData?.deliveryState === "canceled"
                            ? t("canceled.shortName", {
                                  ns: "deliveryStatuses"
                              })
                            : calculateElapsedTime(queueModalData?.createdAt, new Date())}
                    </span>
                </OrderTotalTime>
                <TableTitle>
                    {queueModalData?.deliveryType === "productReturn"
                        ? t("fields.returnPoints", {ns: "common"})
                        : t("fields.deliveryPoints", {ns: "common"})}
                </TableTitle>
                <TableOverflow>
                    <OrderTableNew
                        rowKey="_id"
                        dataSource={queueModalData?.orders}
                        pagination={false}
                        data-testid={`${queueDeliveryType}PointTable`}
                        expandable={{
                            expandedRowRender: record => {
                                return (
                                    <ProductTable
                                        showNote
                                        note={record.note}
                                        dataSource={record.products}
                                        showPaymentInfo={true}
                                        price={record.price}
                                        paymentType={record.paymentType}
                                        barcode={record.barcode}
                                        showBarcode={true}
                                    />
                                );
                            }
                        }}
                    >
                        <Column
                            title={
                                queueModalData?.deliveryType === "productReturn"
                                    ? t("fields.returnPoint", {
                                          ns: "common"
                                      })
                                    : t("fields.customerNameOrReceiptNumber", {
                                          ns: "common"
                                      })
                            }
                            data-testid={`${
                                queueDeliveryType === "return" ? "returnPoint" : "customerName"
                            }`}
                            render={text => text.receiverName || text.receiptNumber}
                        />

                        <Column
                            title={t("fields.phone", {ns: "common"})}
                            data-testid={`phone`}
                            render={text => (text.receiverPhone ? text.receiverPhone : "-")}
                        />
                        <Column
                            title={t("fields.address", {
                                ns: "common"
                            })}
                            data-testid={`address`}
                            render={text => text.receiverAddress}
                        />
                        <Column
                            title={t("fields.addressDetail", {
                                ns: "common"
                            })}
                            data-testid={`addressDetail`}
                            render={text => text.receiverAddressDetail}
                        />
                    </OrderTableNew>
                </TableOverflow>

                <TableTitle>
                    {queueModalData?.deliveryType === "productReturn"
                        ? t("fields.returnPickupPoints", {
                              ns: "common"
                          })
                        : t("fields.pickupPoints", {ns: "common"})}
                </TableTitle>
                <Table
                    rowKey="_id"
                    dataSource={queueModalData?.pickupInfos}
                    pagination={false}
                    data-testid={`collectingPointTable`}
                >
                    <Column
                        title={
                            queueModalData?.deliveryType === "productReturn"
                                ? t("fields.customerName", {
                                      ns: "common"
                                  })
                                : t("fields.warehouseName", {
                                      ns: "common"
                                  })
                        }
                        render={text => <span data-testid="name">{text.name}</span>}
                    />
                    <Column
                        title={t("fields.phone", {ns: "common"})}
                        render={text => (
                            <span data-testid="phoneNumber">{formatPhoneNumber(text.phone)}</span>
                        )}
                    />
                    <Column
                        title={t("fields.address", {ns: "common"})}
                        render={text => <span data-testid="address">{text.address}</span>}
                    />
                </Table>

                {stores.meStore.checkRole("updatePreferredCourier") ? (
                    <CourierSection>
                        <CourierSectionRow>
                            <CourierSectionLabel>
                                {t("fields.preferredCourier", {ns: "common"})}
                            </CourierSectionLabel>
                            <span data-testid="preferredCourierFullName">
                                {queueModalData?.preferredCourier?.fullname}
                            </span>
                        </CourierSectionRow>
                        <CourierSectionRow>
                            <CourierSelect
                                availableCouriers={availableCouriers}
                                excludeId={queueModalData?.preferredCourier?._id}
                                value={preferredCourierValue}
                                onChange={value => setPreferredCourierValue(value)}
                                testId="selectPreferredCourier"
                            />
                        </CourierSectionRow>
                        <CourierSectionRow>
                            <CourierButtonGroup>
                                <Button
                                    disabled={!preferredCourierValue}
                                    type="primary"
                                    data-testid="selectPreferredCourierButton"
                                    onClick={() => {
                                        updatePreferredCourier();
                                    }}
                                    icon={<CheckCircleOutlined />}
                                    block
                                >
                                    {t("actions.updatePreferredCourier", {
                                        ns: "orderDetail"
                                    })}
                                </Button>
                                <Button
                                    type="danger"
                                    data-testid="clearPreferredCourierButton"
                                    onClick={() => {
                                        removePreferredCourier();
                                    }}
                                    icon={<ClearOutlined />}
                                    block
                                >
                                    {t("actions.clearPreferredCourier", {
                                        ns: "orderDetail"
                                    })}
                                </Button>
                            </CourierButtonGroup>
                        </CourierSectionRow>
                    </CourierSection>
                ) : (
                    <></>
                )}

                {stores.meStore.checkRole("canAssignForceCourier") &&
                data?.[queueModalIndex]?.isForceCourierAssignable ? (
                    <CourierSection>
                        <CourierSectionRow>
                            <CourierSectionLabel>
                                {t("fields.forceCourier", {ns: "common"})}
                            </CourierSectionLabel>
                            <span data-testid="forceCourierFullName">
                                {queueModalData?.forceCourier?.fullname}
                            </span>
                        </CourierSectionRow>
                        <CourierSectionRow>
                            <CourierSelect
                                availableCouriers={availableCouriers}
                                excludeId={queueModalData?.forceCourier?._id}
                                value={forceCourierValue}
                                onChange={value => setForceCourierValue(value)}
                                testId="selectedForceCourier"
                            />
                        </CourierSectionRow>
                        <CourierSectionRow>
                            <CourierButtonGroup>
                                <Button
                                    disabled={!forceCourierValue}
                                    type="primary"
                                    data-testid="selectForceCourierButton"
                                    onClick={() => {
                                        updateForceCourier();
                                    }}
                                    icon={<CheckCircleOutlined />}
                                    block
                                >
                                    {"Zorunlu Kuryeyi Güncelle"}
                                </Button>
                                <Button
                                    type="danger"
                                    data-testid="clearForceCourierButton"
                                    onClick={() => {
                                        removeForceCourier();
                                    }}
                                    icon={<ClearOutlined />}
                                    block
                                >
                                    {"Zorunlu Kuryeyi Temizle"}
                                </Button>
                            </CourierButtonGroup>
                        </CourierSectionRow>
                    </CourierSection>
                ) : (
                    <></>
                )}

                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                    <>
                        <TableTitle>{t("fields.company", {ns: "common"})}</TableTitle>
                        <DescriptionContainer column={{xs: 1, sm: 1, md: 2}} bordered>
                            <DescriptionContainer.Item
                                span={2}
                                label={t("fields.companyName", {
                                    ns: "common"
                                })}
                            >
                                <span data-testid="companyName">
                                    {queueModalData?.company?.companyName}
                                </span>
                            </DescriptionContainer.Item>
                            <DescriptionContainer.Item
                                label={t("fields.createdDate", {
                                    ns: "common"
                                })}
                            >
                                <span data-testid="createdAt">
                                    {moment(queueModalData?.company?.createdAt)
                                        .locale(i18n.resolvedLanguage || "tr")
                                        .format("ll")}
                                </span>
                            </DescriptionContainer.Item>
                        </DescriptionContainer>
                    </>
                )}
                {stores.meStore.checkRole("cancelRepeatOrder") &&
                    queueModalData?.orders?.length <= 1 && (
                        <Row justify="end" style={{marginTop: "8px"}}>
                            <Button
                                type="danger"
                                onClick={handleCancelDelivery}
                                data-testid="cancelButton"
                                icon={<CloseCircleOutlined />}
                            >
                                {t("actions.cancelOrder", {ns: "common"})}
                            </Button>
                        </Row>
                    )}
            </QueueModalContainer>
        </StyledQueueModal>
    );
};
